import type {PaymentWay} from '@rocket-ui/lk-api';

export type PaymentWayPreview = {
	id: string;
	wayId: number;
	type: PaymentWay['type'];
	title: string;
	subtitle: string;
	iconUrl: string | null;
	isPrimary: boolean;
};

export type PaymentWayDetailField = {
	label: string;
	value: string;
};

export type PaymentWayDetailsView = {
	title: string;
	iconUrl: string | null;
	fields: PaymentWayDetailField[];
	isPrimary: boolean;
	canBecomePrimary: boolean;
	primaryButtonLabel: string;
};

export const PAYMENT_WAY_PRIMARY_HINT =
	'Эти реквизиты будут использоваться в случае невозможности оплатить по основному способу';

function lastDigits(value: string | null | undefined, count = 4): string | null {
	if (!value) {
		return null;
	}

	const cleaned = value.replace(/\s/g, '');
	const tail = cleaned.slice(-count);

	return tail || null;
}

function bankTitle(way: PaymentWay): string {
	return way.bank?.officialName?.trim() || way.bank?.shortName?.trim() || 'Реквизиты';
}

function formatIbanDisplay(account: string | null | undefined): string {
	if (!account) {
		return '—';
	}

	const cleaned = account.replace(/\s/g, '');

	if (cleaned.length < 8) {
		return cleaned;
	}

	return `${cleaned.slice(0, 14)}**${cleaned.slice(-4)}`;
}

export function formatPaymentWayPreview(way: PaymentWay): PaymentWayPreview {
	const bankName = way.bank?.shortName?.trim() || way.bank?.officialName?.trim() || 'Реквизиты';

	let subtitle = 'Реквизиты';

	if (way.type === 'bank_card') {
		const tail = lastDigits(way.cardNumber);
		subtitle = tail ? `Карта *${tail}` : 'Карта';
	} else if (way.type === 'executor_account/swift') {
		const account = way.account?.replace(/\s/g, '') ?? '';
		subtitle = account.length >= 6 ? `IBAN ${account.slice(0, 2)}**${account.slice(-4)}` : 'IBAN';
	} else if (way.type === 'sbp_account') {
		subtitle = way.phone?.trim() || 'СБП';
	} else {
		const tail = lastDigits(way.account);
		subtitle = tail ? `Счёт *${tail}` : 'Счёт';
	}

	return {
		id: `${way.type}-${way.id}`,
		wayId: way.id,
		type: way.type,
		title: bankName,
		subtitle,
		iconUrl: way.bank?.iconUrl ?? null,
		isPrimary: way.isPrimary,
	};
}

export function mapPaymentWayToDetails(way: PaymentWay): PaymentWayDetailsView {
	const fields: PaymentWayDetailField[] = [];
	let primaryButtonLabel = 'Сделать основным способом';

	if (way.type === 'executor_account/swift') {
		fields.push(
			{label: 'IBAN получателя', value: formatIbanDisplay(way.account)},
			{label: 'SWIFT код', value: way.bank?.bik?.trim() || '—'},
			{label: 'Получатель', value: way.recipient?.trim() || '—'},
		);
		primaryButtonLabel = 'Сделать основным счётом';
	} else if (way.type === 'bank_card') {
		fields.push({label: 'Номер карты', value: way.cardNumber?.trim() || '—'});
		primaryButtonLabel = 'Сделать основной картой';
	} else if (way.type === 'sbp_account') {
		fields.push({label: 'Телефон', value: way.phone?.trim() || '—'});
		if (way.bank?.shortName || way.bank?.officialName) {
			fields.push({label: 'Банк', value: bankTitle(way)});
		}
		primaryButtonLabel = 'Сделать основным способом';
	} else {
		fields.push(
			{label: 'БИК банка', value: way.bank?.bik?.trim() || '—'},
			{label: 'Номер счёта', value: way.account?.trim() || '—'},
		);
		if (way.recipient?.trim()) {
			fields.push({label: 'Получатель', value: way.recipient.trim()});
		}
		primaryButtonLabel = 'Сделать основным счётом';
	}

	return {
		title: bankTitle(way),
		iconUrl: way.bank?.iconUrl ?? null,
		fields,
		isPrimary: way.isPrimary,
		canBecomePrimary: way.canBecomePrimary,
		primaryButtonLabel,
	};
}

export function getVisiblePaymentWays(ways: PaymentWay[] | undefined, limit?: number): PaymentWayPreview[] {
	if (!ways?.length) {
		return [];
	}

	const visible = ways.filter((way) => way.visible !== false);
	const sorted = [...visible].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
	const previews = sorted.map(formatPaymentWayPreview);

	return limit == null ? previews : previews.slice(0, limit);
}
